// AuthContext.tsx
import { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { logout as apiLogout, refresh as apiRefresh, seleccionarSucursal as apiSeleccionarSucursal } from "src/api/authService";
import { TOKEN_KEY, USER_KEY } from "src/api/apiClient";

export interface User {
  idUsuario?: number;
  nombre?: string;
  username?: string;
  /** true cuando es_super_admin = TRUE en tabla seguridad.usuarios */
  esSuperAdmin?: boolean;
  empresa?: {
    idEmpresa: number;
    nombre: string;
  };
  sucursal?: {
    idSucursal: number;
    nombre: string;
  };
  sucursales?: { idSucursal: number; nombre: string }[];
  requiereSeleccionSucursal?: boolean;
  /** Áreas de producción asignadas. Vacío = sin restricción de área. */
  areas?: { idArea: number; nombre: string }[];
  rol?: {
    nombre: string;
    /** Código fijo del rol: SUPER_ADMIN, ADMIN_EMPRESA, ADMIN_SUCURSAL, CAJERO, etc. */
    codigo?: string;
  };
  permisos?: string[]; // Códigos de permiso, '*' = todo (superadmin)
}

export function hasPermiso(user: User | null, codigo: string): boolean {
  if (!user) return false;
  if (user.esSuperAdmin) return true;
  if (user.permisos?.includes("*")) return true;
  return user.permisos?.includes(codigo) ?? false;
}


interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, userData: User, options?: { redirectTo?: string }) => void;
  logout: () => void;
  seleccionarSucursal: (idSucursal: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  const [token, setToken] = useState<string | null>(() => {
    const t = localStorage.getItem("accessToken");
    return t && t.trim() ? t.trim() : null;
  });

  const [user, setUser] = useState<User | null>(() => {
    const raw = localStorage.getItem("accessUser");
    return raw ? JSON.parse(raw) : null;
  });

  const inactivityTimeout = useRef<NodeJS.Timeout | null>(null);

  // --- LOGIN ---
  const login = (newToken: string, userData: User, options?: { redirectTo?: string }) => {
    const t = newToken?.trim();
    if (!t) return;
    localStorage.setItem("accessToken", t);
    localStorage.setItem("accessUser", JSON.stringify(userData));
    setToken(t);
    setUser(userData);
    navigate(options?.redirectTo ?? "/", { replace: true });
  };

  // --- SELECCIONAR SUCURSAL ---
  const seleccionarSucursal = async (idSucursal: number) => {
    // Refrescar token antes de seleccionar para evitar "Token expirado" si la sesión está por vencer
    try {
      const refreshData = await apiRefresh();
      if (refreshData?.restcontrolToken) {
        localStorage.setItem(TOKEN_KEY, refreshData.restcontrolToken);
        setToken(refreshData.restcontrolToken);
        if (refreshData.username) {
          const userData = {
            username: refreshData.username,
            nombre: refreshData.nombre ?? "",
            esSuperAdmin: refreshData.esSuperAdmin ?? (refreshData.idEmpresa == null),
            empresa: refreshData.idEmpresa != null ? { idEmpresa: refreshData.idEmpresa, nombre: refreshData.empresaNombre ?? "" } : undefined,
            sucursal: refreshData.idSucursal != null ? { idSucursal: refreshData.idSucursal, nombre: refreshData.sucursalNombre ?? "" } : undefined,
            sucursales: refreshData.sucursales ?? user?.sucursales,
            requiereSeleccionSucursal: refreshData.requiereSeleccionSucursal ?? false,
            rol: refreshData.rol ? { nombre: refreshData.rol, codigo: refreshData.rolCodigo ?? undefined } : undefined,
            permisos: refreshData.permisos ?? [],
            areas: refreshData.areas ?? user?.areas ?? [],
          };
          localStorage.setItem(USER_KEY, JSON.stringify(userData));
          setUser(userData);
        }
      }
    } catch {
      // Si refresh falla, la sesión expiró; seguirá fallando seleccionarSucursal y el interceptor redirigirá
    }
    const data = await apiSeleccionarSucursal(idSucursal);
    if (data?.restcontrolToken) {
      login(data.restcontrolToken, {
        ...user!,
        sucursal: data.idSucursal != null ? {
          idSucursal: data.idSucursal,
          nombre: data.sucursalNombre ?? "",
        } : undefined,
        sucursales: (data.sucursales?.length ? data.sucursales : user?.sucursales) ?? undefined,
        requiereSeleccionSucursal: false,
        rol: data.rol != null ? { nombre: data.rol } : user?.rol,
        permisos: data.permisos ?? user?.permisos ?? [],
        areas: data.areas ?? user?.areas ?? [],
      });
    }
  };

  // --- LOGOUT ---
  const logout = () => {
    clearInactivityTimer();

    apiLogout()
      .catch(() => {})
      .finally(() => {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("accessUser");
        setToken(null);
        setUser(null);
        navigate("/login", { replace: true });
      });
  };

  // --- INACTIVITY TIMER ---
  const resetInactivityTimer = () => {
    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
    inactivityTimeout.current = setTimeout(() => {
      logout();
      alert("Sesión cerrada por inactividad ⏰");
    }, 7200_000); // 120 min
  };

  const clearInactivityTimer = () => {
    if (inactivityTimeout.current) clearTimeout(inactivityTimeout.current);
  };

  // --- Detectar actividad solo si hay token ---
  useEffect(() => {
    const events = ["mousemove", "keydown", "click"];
    const reset = () => resetInactivityTimer();

    if (token) {
      events.forEach((e) => window.addEventListener(e, reset));
      resetInactivityTimer();
    } else {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearInactivityTimer();
    }

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, seleccionarSucursal }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de un AuthProvider");
  return ctx;
};